import { useEffect, useRef, useState } from "react";
import { Badge, Button, Col, Form, Row, Table } from "react-bootstrap";
import { FaFilePdf, FaPaperclip, FaTimes } from "react-icons/fa";
import Hint from "../shared/Hint.jsx";
import { myobMailtoHref } from "../../utils/myob.js";
import {
  listContractorInvoices,
  createContractorInvoice,
  updateContractorInvoice,
  deleteContractorInvoice,
  uploadContractorInvoiceFile,
  deleteContractorInvoiceFile,
  fileContentUrl,
} from "../../services/apiClient.js";

// Contractor invoices billed against one order — the order's Invoices Received is
// the sum of these. (Legacy GPSContractorInvoice.)
//
// Legacy approval gate: an invoice can only be approved when the order has
// Contract Given > 0 AND its H&S paperwork is settled (H&S N/A, or Risk Assessment
// + Completed Photos both uploaded). Enforced server-side too.
//
// THE DOCUMENT + THE EMAIL (Karen Renall 2026-08-05 #12) — 1.0's
// ContractorInvoice.aspx, ported as it stands:
//   • one file per invoice (OrderInvoiceFile), a save icon that downloads it and
//     a "(3 of 5)" count under the column;
//   • when the invoice CAN be approved, an "Email MYOB To Approve" link, plus a
//     red "Work Complete Not Set" when the order's work-complete box is unticked
//     — note 1.0 warns about that but does NOT block on it (the check is
//     commented out in CanApprovedBeSet), so neither do we.
// Legacy hardcodes the MYOB document-import mailbox and the "MYOB Importer"
// subject. Both are copied verbatim rather than improved (see utils/myob.js,
// shared with Office Expenses since 2026-09-07 — the office bills take the
// same road).

// Rendered in BOTH places an invoice can be approved from. 1.0 puts this only in
// its grid's EDIT template (GetEditApprovedDisplay — the sole occurrence of
// "myob" in the whole solution; GetApprovedDisplay, the read-only twin, has no
// link), and we had ported it onto the read-only ROW instead. That is the wrong
// half: the link's whole purpose is to send the invoice to accounts at the moment
// you are about to tick Approved, and by the time it is approved it has gone.
// Karen Sweatman, 2026-08-12: "when you click approved the myob link should be
// there... you are in that approved screen anyway". The row keeps its copy too —
// it costs nothing and saves opening the editor just to send an email.
function MyobApproveLink({ order }) {
  return (
    <div className="small">
      <a
        href={myobMailtoHref()}
        data-hint="Opens an email to accounts' MYOB importer — attach the invoice document"
      >
        Email MYOB To Approve
      </a>
      {/* 1.0 warns but does NOT block on work-complete (its check is commented
          out in CanApprovedBeSet), so neither do we. */}
      {order && !order.work_complete ? (
        <div className="text-danger fw-bold">Work Complete Not Set</div>
      ) : null}
    </div>
  );
}

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

const EMPTY = { invoice_number: "", amount: "", ignored_amount: "", approved: false, comments: "" };

function InvoicesPanel({ claimId, orderId, order = null, onChanged }) {
  const canApprove =
    !order ||
    (Number(order.contract_given) > 0 &&
      (order.health_safety_na || (order.risk_assessment_uploaded && order.photos_uploaded)));
  const approveHint =
    "Needs Contract Given > 0 and either H&S N/A or both Risk Assessment and Completed Photos uploaded.";
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busyFileId, setBusyFileId] = useState(null);
  // One hidden input, aimed at whichever row's paperclip was clicked.
  const fileInput = useRef(null);
  const uploadTarget = useRef(null);

  function load() {
    return listContractorInvoices(claimId, orderId)
      .then(setItems)
      .catch(() => setError("Unable to load contractor invoices."));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, orderId]);

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  const total = items.reduce((a, it) => a + Number(it.amount || 0), 0);

  function startAdd() {
    setError("");
    setForm(EMPTY);
    setEditingId("new");
  }
  function startEdit(it) {
    setError("");
    setForm({
      invoice_number: it.invoice_number || "",
      amount: it.amount ?? "",
      ignored_amount: it.ignored_amount ?? "",
      approved: !!it.approved,
      comments: it.comments || "",
    });
    setEditingId(it.id);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") await createContractorInvoice(claimId, orderId, form);
      else await updateContractorInvoice(claimId, orderId, editingId, form);
      setEditingId(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    }
  }
  async function remove(it) {
    if (!window.confirm("Delete this contractor invoice?")) return;
    try {
      await deleteContractorInvoice(claimId, orderId, it.id);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  // --- The invoice document ---------------------------------------------------
  function pickFile(it) {
    setError("");
    uploadTarget.current = it.id;
    fileInput.current?.click();
  }
  async function onFileChosen(e) {
    const file = e.target.files?.[0];
    const invId = uploadTarget.current;
    e.target.value = ""; // let the same filename be re-picked after a failure
    if (!file || !invId) return;
    setBusyFileId(invId);
    try {
      await uploadContractorInvoiceFile(claimId, orderId, invId, file);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to attach the invoice.");
    } finally {
      setBusyFileId(null);
    }
  }
  async function removeFile(it) {
    if (!window.confirm(`Remove ${it.invoice_file_name || "this document"} from the invoice?`)) return;
    setBusyFileId(it.id);
    try {
      await deleteContractorInvoiceFile(claimId, orderId, it.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to remove the document.");
    } finally {
      setBusyFileId(null);
    }
  }

  // 1.0's "(3 of 5)" under the File column — how much of this order's paperwork
  // has actually arrived, without counting rows by eye.
  const withFile = items.filter((it) => it.invoice_file_id).length;

  function editRow(key) {
    return (
      <tr key={key}>
        <td colSpan={7} className="bg-body-tertiary">
          <Row className="g-2 align-items-end">
            <Col xs={6} md={3}>
              <Form.Label className="mb-0 small fw-semibold">Invoice #</Form.Label>
              <Form.Control size="sm" value={form.invoice_number} onChange={(e) => set({ invoice_number: e.target.value })} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">Amount</Form.Label>
              <Form.Control size="sm" type="number" step="0.01" value={form.amount} onChange={(e) => set({ amount: e.target.value })} />
              {/* Said BEFORE the save, not only after it (orders.md 3.7, KS
                  2026-09-16): on a row already rolled into a Do & Charge
                  invoice only the amount is locked, so the box stays live for
                  the other fields and this names the one that isn't. */}
              {items.find((i) => i.id === editingId)?.main_invoice_id != null ? (
                <div className="form-text text-warning">On a Do &amp; Charge invoice — void it before changing the amount.</div>
              ) : null}
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">
                <Hint label="Portion of this invoice excluded from the Do & Charge roll-up — 'Yet to invoice' nets it off (legacy IgnoredAmount)">
                  <span style={{ cursor: "help", borderBottom: "1px dotted" }}>Ignored</span>
                </Hint>
              </Form.Label>
              <Form.Control size="sm" type="number" step="0.01" value={form.ignored_amount} onChange={(e) => set({ ignored_amount: e.target.value })} />
            </Col>
            <Col xs={6} md={2} className="d-flex flex-column justify-content-center">
              <Form.Check
                className="mt-3"
                type="checkbox"
                id={`ci-appr-${key}`}
                label="Approved"
                checked={form.approved}
                disabled={!form.approved && !canApprove}
                onChange={(e) => set({ approved: e.target.checked })}
              />
              {/* Where 1.0 actually puts it, and where the job gets done. */}
              {!form.approved && canApprove ? <MyobApproveLink order={order} /> : null}
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="mb-0 small fw-semibold">Comments</Form.Label>
              {/* A BOX, not a line: 1.0's was four rows (ContractorInvoice.aspx
                  txtComments), and on a Do & Charge job this text IS the
                  invoice line the insurer reads — "Electrician: Attend site as
                  directed and complete the pre-start checklist…". A one-line
                  input scrolled it out of sight, so nobody could see where a
                  long description ended (KS 2026-09-15, invoice 84271, a
                  line cut mid-word). No length limit anywhere in 2.0: the
                  column is text and the invoice line wraps whatever it holds. */}
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={form.comments}
                onChange={(e) => set({ comments: e.target.value })}
              />
            </Col>
            {/* The gate must SAY so, not silently disable (Karen 2026-07-15:
                "couldn't figure out how to approve" — the reason only lived in
                a hover tooltip on a disabled checkbox). */}
            {!form.approved && !canApprove ? (
              <Col xs={12}>
                <div className="small text-danger">
                  Can't approve yet: {approveHint}
                </div>
              </Col>
            ) : null}
            <Col xs={12} className="d-flex gap-2">
              <Button size="sm" onClick={save}>{key === "new" ? "Add" : "Save"}</Button>
              <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
            </Col>
          </Row>
        </td>
      </tr>
    );
  }

  // 1.0's File column: the document downloads from the save icon, or "No File
  // Loaded". A legacy row whose BYTES have not been pulled across yet says so
  // rather than offering a link that 404s (the admin "Get from GPS" backfill on
  // the Files tab fetches them).
  function fileCell(it) {
    const busy = busyFileId === it.id;
    if (!it.invoice_file_id) {
      return (
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">No File Loaded</span>
          <Hint label="Attach the contractor's invoice">
            <Button variant="outline-secondary" size="sm" className="py-0 px-1" disabled={busy}
              onClick={() => pickFile(it)} aria-label="Attach the contractor's invoice">
              <FaPaperclip />
            </Button>
          </Hint>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center gap-2">
        {it.invoice_file_has_bytes ? (
          <a href={fileContentUrl(it.invoice_file_id, { download: true })}
            data-hint={`Download ${it.invoice_file_name || "the invoice"}`}
            className="text-nowrap text-decoration-none">
            <FaFilePdf className="text-danger me-1" />
            <span className="small text-truncate d-inline-block align-middle" style={{ maxWidth: 160 }}>
              {it.invoice_file_name || "Invoice"}
            </span>
          </a>
        ) : (
          <Hint label="Held in 1.0 — use Get from GPS on the Files tab to pull the file across">
            <span className="small text-muted text-truncate d-inline-block" style={{ maxWidth: 160, cursor: "help" }}>
              {it.invoice_file_name || "Invoice"} (not held)
            </span>
          </Hint>
        )}
        <Hint label="Remove this document">
          <Button variant="link" size="sm" className="text-danger p-0" disabled={busy}
            onClick={() => removeFile(it)} aria-label="Remove this document">
            <FaTimes />
          </Button>
        </Hint>
      </div>
    );
  }

  function viewRow(it, idx) {
    return (
      <tr key={it.id}>
        <td className="text-muted">{idx + 1}</td>
        <td>{it.invoice_number || <span className="text-muted">—</span>}</td>
        <td className="text-end text-nowrap">{money(it.amount)}</td>
        <td>{fileCell(it)}</td>
        <td className="text-center">
          {it.approved ? (
            <Badge bg="success">Approved</Badge>
          ) : Number(it.amount) > 0 ? (
            <span className="text-danger fw-bold" data-hint="Invoice has an amount but is not approved">!</span>
          ) : (
            <span className="text-muted">No</span>
          )}
          {/* Shows only once the invoice CAN be approved, and disappears the
              moment it is — which is why the editor needs its own copy. */}
          {!it.approved && canApprove ? <MyobApproveLink order={order} /> : null}
        </td>
        <td style={{ whiteSpace: "pre-wrap" }}>{it.comments || <span className="text-muted">—</span>}</td>
        <td className="text-end text-nowrap">
          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => startEdit(it)}>Edit</Button>
          <Button variant="outline-danger" size="sm" onClick={() => remove(it)}>Delete</Button>
        </td>
      </tr>
    );
  }

  return (
    <div className="border rounded p-2 mt-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fw-semibold small">Contractor invoices</span>
        {editingId === null ? (
          <Button size="sm" variant="success" onClick={startAdd}>+ Add Contractor Invoice</Button>
        ) : null}
      </div>
      {error ? <div className="alert alert-danger py-1 my-1">{error}</div> : null}
      {/* One input for every row — the paperclip aims it (uploadTarget). */}
      <input ref={fileInput} type="file" className="d-none" onChange={onFileChosen} />
      <Table bordered hover size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Invoice #</th>
            <th className="text-end">Amount</th>
            <th style={{ width: 230 }}>
              File
              {items.length > 0 ? (
                <span className="ms-1 fw-normal text-muted">({withFile} of {items.length})</span>
              ) : null}
            </th>
            <th className="text-center" style={{ width: 150 }}>Approved</th>
            <th>Comments</th>
            <th className="text-end" style={{ width: 130 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (editingId === it.id ? editRow(it.id) : viewRow(it, idx)))}
          {editingId === "new" ? editRow("new") : null}
          {items.length === 0 && editingId !== "new" ? (
            <tr><td colSpan={7} className="text-center text-muted">No contractor invoices.</td></tr>
          ) : null}
        </tbody>
        {items.length > 0 ? (
          <tfoot>
            <tr className="table-surface">
              <td colSpan={2} className="text-end fw-semibold">Invoices Received</td>
              <td className="text-end text-nowrap fw-semibold">{money(total)}</td>
              <td colSpan={4} />
            </tr>
          </tfoot>
        ) : null}
      </Table>
    </div>
  );
}

export default InvoicesPanel;
